import React, {useMemo} from "react";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {InputLabel, MenuItem, Select, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import useLanguages from "../../hooks/useLanguages";
import {FilterDto} from "../../model/item-containers-models/filterDto";
import {ItemContainerDto} from "../../model/item-containers-models/itemContainerDto";
import I18nDelayedTextField from "../i18n-delayed-text-field";
import ItemContainerBuilderProvider from "./itemContainerBuiderProvider";
import {ItemContainerBuilderRow} from "./ItemContainerBuilderRow";
import {useItemContainerBuilder} from "./useItemContainerBuilder";
import useApi from "../../state/api/useApi";
import {fetchFilters} from "../../state/dashboard-filters/dashboardFilterActions";
import {changeOtherConfigItemContainer} from "../../state/other-config/otherConfigActions";
import {currentItemContainerSelector} from "../../state/other-config/otherConfigSelectors";

function ItemContainerBuilder({isTitleDuplicate}) {
  const itemContainer = useSelector(currentItemContainerSelector);
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const onChange = itemContainer => dispatch(changeOtherConfigItemContainer(itemContainer));
  const {handleRowAdd, getGridContainerProps, getRowContainerProps, asDashboard} = useItemContainerBuilder();

  const hasNodesAssociation = useMemo(() => itemContainer?.configNodes?.length > 0, [itemContainer?.configNodes]);
  const hasHubAssociation = useMemo(() => itemContainer?.configHubs?.length > 0, [itemContainer?.configHubs]);

  return (
    <ItemContainerBuilderProvider>
      {itemContainer && (
        <Grid id="itemContainer-builder__main-container" container spacing={2} justifyContent="flex-start">
          <Grid size={12} id="itemContainer-builder__title-input-container">
            <FormControl fullWidth>
              <I18nDelayedTextField
                label={t("components.itemContainerBuilder.title")}
                error={isTitleDuplicate()}
                helperText={isTitleDuplicate() ? "Duplicate title" : null}
                value={itemContainer.title}
                variant="outlined"
                required
                onChange={value => onChange({...itemContainer, title: value})}
              />
            </FormControl>
          </Grid>
          {!asDashboard && (
            <Grid size={12}>
              <Grid alignItems="center" sx={{display: "flex"}}>
                <FormControlLabel
                  label={t("components.itemContainerBuilder.checkbox.isPublic.label")}
                  labelPlacement="end"
                  control={
                    <Checkbox
                      id="item-container-builder__is-public-checkbox"
                      checked={!itemContainer.isPrivate || false}
                      onChange={(_, checked) => onChange({...itemContainer, isPrivate: !checked})}
                    />
                  }
                />
                {itemContainer.isPrivate && (hasHubAssociation || hasNodesAssociation) && (
                  <Tooltip
                    title={
                      hasHubAssociation
                        ? t("components.itemContainerBuilder.checkbox.isPublic.infoTooltip.hubAssociation")
                        : t("components.itemContainerBuilder.checkbox.isPublic.infoTooltip.nodeAssociation")
                    }
                  >
                    <WarningAmberOutlinedIcon sx={{color: "#ed6c02", marginLeft: "6px"}} />
                  </Tooltip>
                )}
              </Grid>
              <Grid alignItems="center" sx={{display: "flex"}}>
                <FormControlLabel
                  label={t("components.itemContainerBuilder.checkbox.hideTitle")}
                  labelPlacement="end"
                  control={
                    <Checkbox
                      id="item-container-builder__hide-title-checkbox"
                      checked={itemContainer.hideTitle}
                      onChange={(_, checked) => onChange({...itemContainer, hideTitle: checked})}
                    />
                  }
                />
              </Grid>
            </Grid>
          )}
          {asDashboard && <FilterContainer itemContainer={itemContainer}></FilterContainer>}
          <Grid
            id="itemContainer-builder__items-container"
            size={12}
            style={{pointerEvents: "auto"}}
            {...getGridContainerProps()}
          >
            <Grid size={12} style={{margin: "0px -8px 20px"}}>
              {(itemContainer.item || []).map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    pointerEvents: "auto",
                    marginBottom: "8px"
                  }}
                  {...getRowContainerProps(idx)}
                >
                  <ItemContainerBuilderRow row={row} rowIdx={idx} />
                </div>
              ))}
            </Grid>
          </Grid>
          <Grid
            sx={{
              width: "100%",
              height: "100%",
              display: "inline-block",
              verticalAlign: "middle"
            }}
          >
            <Grid container justifyContent="center">
              <Button id="item-container-builder__add-row-btn" endIcon={<AddIcon />} onClick={handleRowAdd}>
                {t("components.itemContainerBuilder.actions.addRow")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </ItemContainerBuilderProvider>
  );
}

const FilterContainer = ({itemContainer}: {itemContainer: ItemContainerDto}) => {
  const {localizeI18nObj, t} = useLanguages();
  const {handleFilterChange, handleFilterLevelsChange} = useItemContainerBuilder();

  const {data} = useApi<FilterDto[]>(fetchFilters(false), {
    invokeImmediately: true,
    enableDeduplication: true
  });

  return (
    <Grid container alignItems="center" sx={{marginTop: "12px"}}>
      <Grid>
        <FormControl id="item-container-builder__select-filter" variant="outlined" sx={{minWidth: "180px"}}>
          <InputLabel id="filter-label">{t("components.filterContainer.title")}</InputLabel>
          <Select
            labelId="filter-label"
            label={t("components.filterContainer.title")}
            style={{minWidth: 125}}
            onChange={({target}) => handleFilterChange(data?.find(({id}) => id === target.value))}
            value={itemContainer?.filter?.id | 0}
          >
            <MenuItem value="0">
              <em>{t("components.map.layers.none")}</em>
            </MenuItem>
            {data &&
              data.map(({id, name}) => (
                <MenuItem key={id} value={id}>
                  {localizeI18nObj(name)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      {itemContainer?.filter &&
        itemContainer.filter.levels.map(({level, label}, index) => (
          <Grid key={index} sx={{marginLeft: "16px"}}>
            <FormControlLabel
              value={level}
              sx={{
                marginLeft: "0px",
                marginRight: "0px"
              }}
              label={localizeI18nObj(label)}
              control={
                <Checkbox
                  id={`item-container-builder__checkbox-filter-level${level}`}
                  checked={itemContainer.filterLevelsSelected.includes(level)}
                  style={{padding: 5}}
                  onChange={({target}, checked) => handleFilterLevelsChange(checked, Number(target.value))}
                />
              }
            />
          </Grid>
        ))}
    </Grid>
  );
};

export default ItemContainerBuilder;
